import { Type } from "@sinclair/typebox";
import { type Context, Hono } from "hono";
import type { ContentfulStatusCode } from "hono/utils/http-status";
import type { CommonLogger } from "../interfaces.js";
import { validate } from "../validation.js";

interface TestRoutesOptions {
  logger: CommonLogger;
}

// Local implementation of the test-proxy contract (zuplo/core apps/test-proxy,
// POST /v1/test) so the embedded portal's test console works against the local
// gateway. The target host + path arrive folded into a single absolute `url`;
// the target's response is 200-wrapped, and proxy-level failures are returned
// as non-2xx problem+json flagged with the `x-test-proxy-error` header — see
// src/query/testProxy.ts in the portal for the client side of this contract.
const TestRequestBody = Type.Object({
  url: Type.String(),
  method: Type.String(),
  deployment: Type.Optional(Type.String()),
  headers: Type.Optional(Type.Record(Type.String(), Type.String())),
  body: Type.Optional(Type.String()),
});

/**
 * Emit a proxy-LEVEL failure exactly as the real test-proxy does: a non-2xx
 * problem+json body flagged with the `x-test-proxy-error` header carrying the
 * error code (never a 200-wrapped relay). Mirroring these locally means bad
 * requests surface identically in `zuplo dev` and in staging/prod instead of
 * being masked as upstream failures.
 */
function sendProxyError(
  c: Context,
  {
    status,
    code,
    title,
    detail,
    instance,
  }: {
    status: ContentfulStatusCode;
    code: string;
    title: string;
    detail: string;
    instance?: string;
  },
) {
  return c.body(
    JSON.stringify({
      type: `https://zuplo.com/problems/${code}`,
      title,
      status,
      code,
      detail,
      ...(instance ? { instance } : {}),
    }),
    status,
    {
      "x-test-proxy-error": code,
      "content-type": "application/problem+json",
    },
  );
}

/**
 * The relay target must be an absolute http(s) URL — the portal folds the
 * target host + path into `url` (buildTestProxyTargetUrl). A relative path or a
 * non-http protocol can't be relayed, so reject it up front rather than letting
 * `fetch` throw and mislabel it as an upstream failure.
 */
function isAbsoluteHttpUrl(value: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:";
}

export function createTestRoutes(options: TestRoutesOptions): Hono {
  const { logger } = options;
  const app = new Hono();

  // Test endpoint that proxies requests to the local Zuplo gateway
  app.post("/", validate("json", TestRequestBody), async (c) => {
    const { url, method, deployment, headers = {}, body } = c.req.valid("json");

    logger.info({ deployment }, `Proxying test request: ${method} ${url}`);

    if (!isAbsoluteHttpUrl(url)) {
      return sendProxyError(c, {
        status: 400,
        code: "invalid_request",
        title: "Invalid Request",
        detail: `The request URL must be an absolute http(s) URL: ${url}`,
      });
    }

    const isGetOrHead = ["GET", "HEAD"].includes(method.toUpperCase());
    if (isGetOrHead && body) {
      return sendProxyError(c, {
        status: 400,
        code: "get_or_head_with_body",
        title: "Invalid Request",
        detail: "GET and HEAD requests can't include a body.",
      });
    }

    // Clean headers - remove host and other problematic headers
    const cleanHeaders: Record<string, string> = {};
    for (const [key, value] of Object.entries(headers)) {
      const lowerKey = key.toLowerCase();
      // Skip headers that shouldn't be forwarded
      if (lowerKey !== "host" && lowerKey !== "connection") {
        cleanHeaders[key] = value;
      }
    }

    const fetchOptions: RequestInit = {
      method,
      headers: cleanHeaders,
    };
    if (body && !isGetOrHead) {
      fetchOptions.body = body;
    }

    // Make the request to the local gateway
    let response: Response;
    try {
      response = await fetch(url, fetchOptions);
    } catch (err) {
      if (!(err instanceof TypeError)) {
        logger.error({ err, method, url }, "Error proxying test request");
      } else {
        logger.error({ method, url }, "Error proxying test request");
      }

      return sendProxyError(c, {
        status: 502,
        code: "upstream_request_failed",
        title: "Local Gateway Unavailable",
        detail:
          "Could not connect to the local Zuplo gateway. Make sure your local gateway is running.",
        instance: url,
      });
    }

    // Capture response
    const responseBody = await response.text();
    const responseHeaders: Record<string, string> = {};

    // Convert headers to plain object
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    // Return test result in the format expected by the frontend
    return c.json({
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      body: responseBody,
    });
  });

  return app;
}
